import { AiAgentBundleId, AiHomeFeature } from '@euler/model/ai-home';
import {
  BadgePercent,
  CalendarDays,
  ChartColumn,
  ChartLine,
  HandCoins,
  ImagePlus,
  LucideIcon,
  MessagesSquare,
  Receipt,
  Ticket,
  TrendingUp,
  Upload,
  Users,
} from 'lucide-react-native';

/** The 知车 AI palette, from the AI home and chat designs. */
export const aiColors = {
  background: '#F4F6FA',
  foreground: '#0F172A',
  primary: '#1A6FD8',
  primaryForeground: '#FFFFFF',
  secondary: '#E8EFFA',
  secondaryForeground: '#1E3A5F',
  muted: '#EDF1F6',
  mutedForeground: '#64748B',
  accent: '#E3F6EE',
  accentForeground: '#0B6B4A',
  destructive: '#DC2626',
  card: '#FFFFFF',
  border: '#E2E8F0',
  input: '#F1F5F9',
  skeleton: '#EEF1F5',
  blue: '#1A6FD8',
  green: '#10B981',
  amber: '#F59E0B',
  violet: '#8B5CF6',
  red: '#EF4444',
} as const;

/** The AI home's shadows: a card resting on the page, and one lifted over
 * the header. */
export const aiElevation = {
  card: {
    shadowColor: '#0F172A',
    shadowOpacity: 0.07,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
  },
  raised: {
    shadowColor: '#0F172A',
    shadowOpacity: 0.12,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
} as const;

/** The AI home header's blue, lighter at the top left. */
export const kAiHeroGradient: string[] = [
  '#3B8DF0',
  aiColors.primary,
  '#1456B0',
];

/** A translucent tint of a palette color, e.g. `tint(aiColors.green, 0.1)`. */
export function tint(hex: string, alpha: number): string {
  const value = parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${
    value & 255
  }, ${alpha})`;
}

/** What a tool's card on AI home shows. */
interface AiSuiteToolCard {
  feature: AiHomeFeature;
  title: string;
  subtitle: string;
  color: string;
  icon: LucideIcon;
}

/** A tool that is a chat with an agent, and the focus such a chat shows. */
export interface AiSuiteTool extends AiSuiteToolCard {
  kind: 'chat';
  /** The agent a chat opened from this tool talks to. */
  bundleId: AiAgentBundleId;
  /** The question an empty chat focused on this tool opens with. */
  heading: string;
  /** The assistant's opening line in an empty chat focused on this tool. */
  intro: string;
  /** The composer's hint in a chat focused on this tool, in plain words. */
  placeholder: string;
  starters: AiSuiteStarter[];
}

/** A tool that is a page of its own, with no chat: 朋友圈海报. */
export interface AiSuitePageTool extends AiSuiteToolCard {
  kind: 'page';
}

export type AiSuiteHomeTool = AiSuiteTool | AiSuitePageTool;

/** A request an empty chat offers to send for the user. */
export interface AiSuiteStarter {
  text: string;
  icon: LucideIcon;
  color: string;
}

export const kAiSuiteTools: AiSuiteHomeTool[] = [
  {
    kind: 'chat',
    feature: AiHomeFeature.Apdm,
    title: '私域营销',
    subtitle: '精准触达老客户',
    color: aiColors.blue,
    icon: Users,
    bundleId: AiAgentBundleId.Apdm,
    heading: '想要开始一个什么样的营销活动？',
    intro: '告诉我想联系哪些客户、给什么优惠，我来拟文案和活动，确认后再发送。',
    // Starting a plan and changing one are both said here.
    placeholder: '说说想联系哪些客户，或想改哪里…',
    starters: [
      {
        text: '给半年没到店的客户发一张保养优惠券',
        icon: Ticket,
        color: aiColors.blue,
      },
      {
        text: '提醒两周内保养到期的客户预约',
        icon: CalendarDays,
        color: aiColors.green,
      },
      {
        text: '上传客户表格，给他们发一条问候',
        icon: Upload,
        color: aiColors.violet,
      },
    ],
  },
  {
    kind: 'chat',
    feature: AiHomeFeature.Coaching,
    title: '销售对练',
    subtitle: '话术训练与情景演练',
    color: aiColors.green,
    icon: MessagesSquare,
    bundleId: AiAgentBundleId.SalesCoaching,
    heading: '想练习哪个销售场景？',
    intro: '描述一个销售场景，我扮演客户陪你练习，结束后给出点评。',
    placeholder: '说一个想练的销售场景…',
    starters: [
      {
        text: '客户嫌刹车保养太贵，我该怎么回应',
        icon: HandCoins,
        color: aiColors.amber,
      },
      {
        text: '练习向老客户推荐年度保养套餐',
        icon: BadgePercent,
        color: aiColors.blue,
      },
      {
        text: '客户只想做最便宜的方案，怎么引导',
        icon: MessagesSquare,
        color: aiColors.green,
      },
    ],
  },
  {
    kind: 'chat',
    feature: AiHomeFeature.BusinessIntelligence,
    title: '经营分析',
    subtitle: '数据洞察与报表',
    color: aiColors.amber,
    icon: ChartColumn,
    bundleId: AiAgentBundleId.Workshop,
    heading: '想了解门店的哪些数据？',
    intro: '问我门店的营收、工单和客户数据，我会用图表回答。',
    placeholder: '想看哪项数据，直接问…',
    starters: [
      { text: '本周工单营收是多少', icon: Receipt, color: aiColors.blue },
      {
        text: '上个月哪些服务项目最赚钱',
        icon: TrendingUp,
        color: aiColors.green,
      },
      {
        text: '最近一个月的到店客户趋势',
        icon: ChartLine,
        color: aiColors.violet,
      },
    ],
  },
  {
    kind: 'page',
    feature: AiHomeFeature.Posters,
    title: '朋友圈海报',
    subtitle: '社交海报设计',
    color: aiColors.violet,
    icon: ImagePlus,
  },
];

function isChatTool(tool: AiSuiteHomeTool): tool is AiSuiteTool {
  return tool.kind === 'chat';
}

const kChatTools = kAiSuiteTools.filter(isChatTool);

export function aiSuiteToolForBundle(
  bundleId: AiAgentBundleId | null | undefined,
): AiSuiteTool | null {
  return kChatTools.find(tool => tool.bundleId === bundleId) ?? null;
}

/** The chip a conversation shows; the general assistant has none. */
export function aiSuiteToolForSession(
  bundleId: AiAgentBundleId,
): AiSuiteTool | null {
  return bundleId === AiAgentBundleId.Workshop
    ? null
    : aiSuiteToolForBundle(bundleId);
}

/** The chat focus for a feature; none for a tool that is a page. */
export function aiSuiteToolForFeature(
  feature: AiHomeFeature,
): AiSuiteTool | null {
  return kChatTools.find(tool => tool.feature === feature) ?? null;
}
